import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/3ed0734e/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import { StructuredListWrapper, StructuredListHead, StructuredListBody, StructuredListRow, StructuredListInput, StructuredListCell, OrderedList, ListItem } from 'carbon-components-react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">How it works</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility considerations</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Resources</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility testing</AnchorLink>
    </AnchorLinks>
    <h2 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h2>
    <p>{`The modal Carbon component is a secondary window that displays over the primary window to allow the user to maintain the context of a particular task. When the modal is displayed the primary window is inert, so users cannot interact with content outside an active modal.`}</p>
    <p>{`The modal dialog contains the tab sequence so that `}<inlineCode parentName="p">{`Tab`}</inlineCode>{` and `}<inlineCode parentName="p">{`Shift-Tab`}</inlineCode>{` keys do not move the focus outside of the modal. To support screen reader users each modal component includes a WAI ARIA `}<inlineCode parentName="p">{`role="dialog"`}</inlineCode>{` and `}<inlineCode parentName="p">{`aria-modal="true"`}</inlineCode>{` so assistive technologies understand that the windows underneath the current dialog are not available for interaction (inert). Focus, accessible descriptions, and labels are set based on the content of each modal. Modals that contain static text have the `}<inlineCode parentName="p">{`aria-describeby`}</inlineCode>{` property set on the element with the ARIA `}<inlineCode parentName="p">{`role="dialog"`}</inlineCode>{` to indicate which element or elements in the dialog contain content describes the primary purpose or message of the dialog. A value is also set for the `}<inlineCode parentName="p">{`aria-labelledby`}</inlineCode>{` property that refers to a visible dialog title.`}</p>
    <h2 {...{
      "id": "accessibility-considerations"
    }}>{`Accessibility considerations`}</h2>
    <p>{`This component has been validated to meet the `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/WCAG20/"
      }}>{`WCAG 2.0 AA`}</a>{` and `}<a parentName="p" {...{
        "href": "http://www.section508.gov/"
      }}>{`Section 508`}</a>{` accessibility guidelines, however changes made by the content owner can affect accessibility compliance. Be sure to review and follow the guidance in this section when updating or adding new content to this component.`}</p>
    <ol>
      <li parentName="ol">{`After the modal opens, initial focus should be set on the first focusable element in the modal. See WAI-ARIA Authoring Practices `}<a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-practices-1.1/examples/dialog-modal/dialog.html"
        }}>{`Modal Dialog Example, Accessibility Features section`}</a>{` for best practices on setting focus.`}</li>
      <li parentName="ol">{`After a modal closes, focus should retain the user’s point of regard and return to the element that invoked the modal.`}</li>
      <li parentName="ol">{`Focus must not move outside the modal until it is closed.`}</li>
      <li parentName="ol">{`It is strongly recommended that the tab sequence of all modal dialogs include a visible element with button role that closes the dialog, such as a close icon or cancel button.`}</li>
      <li parentName="ol">{`Use the Alert modal dialog for special case that interrupts user’s workflow to communicate a brief, important message and require a user’s response,`}</li>
    </ol>
    <h2 {...{
      "id": "resources"
    }}>{`Resources`}</h2>
    <p><strong parentName="p">{`Helpful resources for creating customized accessible implementations`}</strong></p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#dialog_modal"
        }}>{`W3C WAI-ARIA Authoring Practices Dialog Modal Design Pattern`}</a>{` covers the usage of ARIA names, state and roles, as well as the expected keyboard intractions.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.ibm.com/able/guidelines/ci162/accessibility_checklist.html"
        }}>{`IBM Accessibility Checklist Checkpoint`}</a>{`:`}<ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.ibm.com/able/guidelines/ci162/no_keyboard_trap.html"
            }}>{`1.4.13 Content on Hover or Focus`}</a>{` (WCAG Success Criteria `}<a parentName="li" {...{
              "href": "https://www.w3.org/WAI/WCAG21/Understanding/content-on-hover-or-focus.html"
            }}>{`1.4.13`}</a>{`)`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.ibm.com/able/guidelines/ci162/no_keyboard_trap.html"
            }}>{`2.1.2 No Keyboard Traps`}</a>{` (WCAG Success Criteria `}<a parentName="li" {...{
              "href": "https://www.w3.org/TR/UNDERSTANDING-WCAG20/keyboard-operation-trapping.html"
            }}>{`2.1.2`}</a>{`)`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.ibm.com/able/guidelines/ci162/link_purpose.html"
            }}>{`2.4.3 Focus Order`}</a>{` (WCAG Success Criteria `}<a parentName="li" {...{
              "href": "https://www.w3.org/WAI/WCAG21/Understanding/focus-order"
            }}>{`2.4.3`}</a>{`)`}</li>
        </ul></li>
    </ul>
    <h2 {...{
      "id": "accessibility-testing"
    }}>{`Accessibility testing`}</h2>
    <p>{`Accessibility issues are tracked in the `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon/milestones"
      }}>{`Carbon Component GitHub repository`}</a>{`.`}</p>
    <h3 {...{
      "id": "automated-test"
    }}>{`Automated test`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">
            Automated test environment
          </StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - macOS Mojave version 10.14.6 with VoiceOver
            <br />
            - Chrome version 77.0.3865.90
            <br />
            - Dynamic Assessment Plugin version 1.8.0.0 - IBM Accessibility WCAG
            2.1 Sept. 2019 Ruleset
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>DAP test</strong>
            <br />- No violations
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3 {...{
      "id": "macos-screen-reader-tests"
    }}>{`macOS Screen reader tests`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">Environment </StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - macOS Mojave version 10.14.6 with VoiceOver
            <br />
            - Chrome version 77.0.3865.90
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>VoiceOver(VO) test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Tab to Launch modal button. VO announces the button label.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Enter or Space key to launch the modal. VO announces the
                button label, headings, and text.
              </ListItem>
              <ListItem mdxType="ListItem">
                Tab or Shift-Tab navigate through the modal buttons and
                announces each label.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Control-Option-Right Arrow or Left Arrow to navigate
                through the modal message.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select the Esc key, Close button or Cancel button to close the
                modal and return focus to the Launch modal button in the primary
                window.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - macOS Mojave version 10.14.6 with VoiceOver
            <br />
            - Safari version 13.0.2
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>VoiceOver(VO) test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Tab to Launch modal button. VO announces the button label.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Enter or Space key to launch the modal. VO announces the
                Button label.
              </ListItem>
              <ListItem mdxType="ListItem">
                Tab or Shift-Tab navigate through the modal buttons and
                announces each label.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Control-Option-Right Arrow or Left Arrow to navigate
                through the modal message.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select the Esc key, Close button or Cancel button to close the
                modal and return focus to the Launch modal button in the primary
                window.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3 {...{
      "id": "windows-screen-reader-tests"
    }}>{`Windows screen reader tests`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">Environment</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - Microsoft Windows 10
            <br />
            - Firefox version 67
            <br />
            - JAWS version 19.1810.64
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>JAWS test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Tab to Launch modal button. JAWS announces the button label.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Enter or Space key to launch the modal.
              </ListItem>
              <ListItem mdxType="ListItem">
                Tab or Shift-Tab to navigate through the modal buttons and
                announces each label. Focus does not move out of the modal when
                tabbing.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Control-Option-Right Arrow or Left Arrow to navigate
                through the modal message.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press Close button or Cancel button to close the modal and
                return focus to the Launch modal button in the primary window.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - Microsoft Windows 10
            <br />
            - Chrome version 73.0.3683.103 (Official Build) (64-bit)
            <br />
            - JAWS version 19.1810.64
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>JAWS test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Tab to Launch modal button. JAWS announces the button label.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Enter or Space key to launch the modal.
              </ListItem>
              <ListItem mdxType="ListItem">
                Tab or Shift-Tab to navigate through the modal buttons and
                announces each label. Focus does not move out of the modal when
                tabbing.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Control-Option-Right Arrow or Left Arrow to navigate
                through the modal message.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press Close button or Cancel button to close the modal and
                return focus to the Launch modal button in the primary window.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - Microsoft Windows 10
            <br />
            - Chrome version 73.0.3683.103 (Official Build) (64-bit)
            <br />
            - NVDA Version 2018.4.1
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>NVDA test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Tab to Launch modal button. NVDA announces the button label
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Enter or Space key to launch the modal. NVDA announces
                the text and buttons automatically.
              </ListItem>
              <ListItem mdxType="ListItem">
                Tab or Shift-Tab to navigate through the modal buttons and
                announces each label. Focus does not move out of the modal when
                tabbing.
              </ListItem>
              <ListItem mdxType="ListItem">
                Select Control-Option-Right Arrow or Left Arrow to navigate
                through the modal message.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press Close button or Cancel button to close the modal and
                return focus to the Launch modal button in the primary window.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3 {...{
      "id": "android-screen-reader-tests"
    }}>{`Android screen reader tests`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListRow head mdxType="StructuredListRow">
        <StructuredListCell head mdxType="StructuredListCell">Environment </StructuredListCell>
        <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
      </StructuredListRow>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - Android version 9 with Talkback
            <br />
            - Chrome Version 73.0.3683.103 (Official Build) (64-bit)
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>Talkback test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Swipe left or right, or use the Alt+Arrow keys in quick nav mode
                until you find the Launch modal button.
              </ListItem>
              <ListItem mdxType="ListItem">
                Double tap the button or press the Alt-Enter keys to launch the
                modal. The close modal button is announces and has focus.
              </ListItem>
              <ListItem mdxType="ListItem">
                Swipe Left or Right to explore the modal. VO announces the text,
                and focus on the Cancel and Save buttons.
              </ListItem>
              <ListItem mdxType="ListItem">
                Note: that navigation is not restricted to the modal only, after
                the modal is open there is no way to know if there is an open
                modal present, the user needs to remember it. Doubletap or press
                Alt+Enter on the Close or Cancel buttons to close the modal.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3 {...{
      "id": "ios-screen-reader-tests"
    }}>{`iOS screen reader tests`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListRow head mdxType="StructuredListRow">
        <StructuredListCell head mdxType="StructuredListCell">Environment </StructuredListCell>
        <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
      </StructuredListRow>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - iOS version 13.1.3 with VoiceOver
            <br />
            - Safari version 13.1.3
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>VoiceOver test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Swipe left or right, or use the Arrow keys in quick nav mode
                until you find the Launch modal button.
              </ListItem>
              <ListItem mdxType="ListItem">
                Double tap the button to launch the modal. The close modal
                button is announces and has focus.
              </ListItem>
              <ListItem mdxType="ListItem">
                Swipe Left or Right to explore the modal. VO announces the text,
                and focus on the Cancel and Save buttons.
              </ListItem>
              <ListItem mdxType="ListItem">
                After Double tapping on the close or cancel button, the modal
                closes but focus is stuck on the selected button. Touch the
                screen anywhere to move out of the focus trap.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      